import React, { useLayoutEffect, useRef } from "react";

import ui from "config/i18n/ui";
import useLang from "hooks/useLang";

/**
 * <text> that is squeezed to maxWidth only when the rendered string is
 * longer (translations of the labels vary a lot in length; the diagram has
 * fixed geometry). Short strings keep their natural width.
 */
const FitText: React.FC<
  React.SVGProps<SVGTextElement> & { maxWidth: number; children: string }
> = ({ maxWidth, children, ...props }) => {
  const ref = useRef<SVGTextElement>(null);
  useLayoutEffect(() => {
    let alive = true;
    const measure = () => {
      const el = ref.current;
      if (!alive || !el) return;
      el.removeAttribute("textLength");
      el.removeAttribute("lengthAdjust");
      if (el.getComputedTextLength() > maxWidth) {
        el.setAttribute("textLength", String(maxWidth));
        el.setAttribute("lengthAdjust", "spacingAndGlyphs");
      }
    };
    measure();
    // Inter loads with font-display: swap – on a first visit the first
    // measurement uses the fallback font, so measure again once it is in.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(measure);
    fonts?.addEventListener?.("loadingdone", measure);
    return () => {
      alive = false;
      fonts?.removeEventListener?.("loadingdone", measure);
    };
  }, [children, maxWidth]);
  return (
    <text ref={ref} {...props}>
      {children}
    </text>
  );
};

interface BoxProps {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  lines?: string[];
  stroke?: string;
  fill?: string;
}

const Box: React.FC<BoxProps> = ({
  x,
  y,
  w,
  h,
  title,
  lines = [],
  stroke,
  fill,
}) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={12}
      fill={fill || "rgba(255,255,255,0.05)"}
      stroke={stroke || "rgba(255,255,255,0.3)"}
      strokeWidth={1.5}
    />
    <FitText
      x={x + w / 2}
      y={y + 24}
      textAnchor="middle"
      fontSize={15}
      fontWeight={700}
      fill="#ffffff"
      maxWidth={w - 16}
    >
      {title}
    </FitText>
    {lines.map((line, i) => (
      <FitText
        key={line}
        x={x + w / 2}
        y={y + 44 + i * 15}
        textAnchor="middle"
        fontSize={11.5}
        fill="rgba(255,255,255,0.72)"
        maxWidth={w - 14}
      >
        {line}
      </FitText>
    ))}
  </g>
);

interface LabelProps {
  x: number;
  y: number;
  text: string;
  anchor?: "start" | "middle" | "end";
  /** Room available for the label (px in diagram units). */
  max?: number;
}

const Label: React.FC<LabelProps> = ({
  x,
  y,
  text,
  anchor = "middle",
  max = 400,
}) => (
  <FitText
    x={x}
    y={y}
    textAnchor={anchor}
    fontSize={11}
    fontWeight={600}
    fill="rgba(255,255,255,0.8)"
    maxWidth={max}
  >
    {text}
  </FitText>
);

interface Props {
  ariaLabel: string;
}

/**
 * MS Trader data-flow diagram (inline SVG, dark background). Technical
 * component names are kept in English on purpose; the explanatory caption
 * next to it is localised by the page.
 */
const MsTraderDiagram: React.FC<Props> = ({ ariaLabel }) => {
  const d = ui[useLang()].diagram;
  const line = "rgba(255,255,255,0.6)";
  const blue = "#4f8dff";
  const mint = "#7ff1ab";

  return (
    <svg
      viewBox="0 0 1000 600"
      role="img"
      aria-label={ariaLabel}
      fontFamily="inherit"
    >
      <defs>
        <marker
          id="mst-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill={line} />
        </marker>
        <marker
          id="mst-arrow-blue"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill={blue} />
        </marker>
        <marker
          id="mst-arrow-mint"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill={mint} />
        </marker>
      </defs>

      {/* Kubernetes boundary */}
      <rect
        x={240}
        y={36}
        width={520}
        height={528}
        rx={18}
        fill="rgba(5,98,255,0.05)"
        stroke="rgba(79,141,255,0.35)"
        strokeWidth={1.2}
        strokeDasharray="6 5"
      />
      <Label x={260} y={58} text={d.cluster} anchor="start" />

      {/* Tunnel boundary to the exchange */}
      <line
        x1={782}
        y1={60}
        x2={782}
        y2={560}
        stroke="rgba(127,241,171,0.4)"
        strokeWidth={1.2}
        strokeDasharray="4 5"
      />
      <FitText
        x={782}
        y={588}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="rgba(127,241,171,0.85)"
        maxWidth={200}
      >
        {d.tunnel}
      </FitText>

      {/* Trader */}
      <Box
        x={20}
        y={250}
        w={170}
        h={80}
        title={d.trader}
        lines={[d.browser, "ms-trader-ui"]}
      />

      {/* mslogin */}
      <Box x={270} y={80} w={190} h={70} title="mslogin" lines={[d.mslogin]} />

      {/* gateway */}
      <Box
        x={270}
        y={230}
        w={190}
        h={110}
        title="gateway"
        lines={d.gateway}
        stroke={blue}
        fill="rgba(79,141,255,0.12)"
      />

      {/* mstrader DB */}
      <Box
        x={270}
        y={450}
        w={190}
        h={70}
        title="mstrader"
        lines={[d.mstrader]}
        fill="rgba(5,98,255,0.14)"
      />

      {/* fixproxy */}
      <Box
        x={540}
        y={150}
        w={190}
        h={80}
        title="fixproxy"
        lines={[d.perExchange, d.fanout]}
      />

      {/* mdcollector */}
      <Box
        x={540}
        y={330}
        w={190}
        h={80}
        title="mdcollector"
        lines={[d.perExchange, d.writesHistory]}
      />

      {/* msmarketdata DB */}
      <Box
        x={540}
        y={450}
        w={190}
        h={70}
        title="msmarketdata"
        lines={[d.msmarketdata]}
        fill="rgba(5,98,255,0.14)"
      />

      {/* Exchange */}
      <Box
        x={805}
        y={80}
        w={175}
        h={380}
        title={d.exchange}
        lines={d.exchangeLines}
        stroke={mint}
        fill="rgba(127,241,171,0.06)"
      />
      <Box
        x={825}
        y={165}
        w={135}
        h={100}
        title={d.marketData}
        lines={[d.fixLines]}
        stroke="rgba(127,241,171,0.5)"
      />
      <Box
        x={825}
        y={300}
        w={135}
        h={70}
        title={d.trading}
        lines={[d.fixLine]}
        stroke="rgba(127,241,171,0.5)"
      />

      {/* Trader → mslogin (login) */}
      <path
        d="M150,250 L300,150"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow)"
      />
      <Label x={160} y={186} text={d.login} anchor="start" max={195} />

      {/* mslogin → gateway */}
      <path
        d="M365,150 L365,230"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow)"
      />
      <Label x={375} y={195} text={d.spawn} anchor="start" max={160} />

      {/* Trader ↔ gateway */}
      <path
        d="M190,300 L270,300"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerStart="url(#mst-arrow)"
        markerEnd="url(#mst-arrow)"
      />
      <Label x={230} y={318} text={d.restWs} max={110} />

      {/* gateway → exchange trading */}
      <path
        d="M460,290 L825,335"
        fill="none"
        stroke={blue}
        strokeWidth={1.8}
        markerStart="url(#mst-arrow-blue)"
        markerEnd="url(#mst-arrow-blue)"
      />
      <Label x={640} y={300} text={d.tradingSession} max={250} />

      {/* exchange MD → fixproxy */}
      <path
        d="M825,200 L730,195"
        fill="none"
        stroke={mint}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow-mint)"
      />
      <Label x={778} y={186} text={d.mdSession} max={88} />

      {/* exchange MD → mdcollector */}
      <path
        d="M825,240 L775,240 L775,370 L730,370"
        fill="none"
        stroke={mint}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow-mint)"
      />
      <Label x={770} y={262} text={d.md} anchor="end" max={40} />

      {/* fixproxy → gateway */}
      <path
        d="M540,200 L460,258"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow)"
      />
      <Label x={505} y={213} text={d.mdFeed[0]} anchor="end" max={135} />
      <Label x={505} y={226} text={d.mdFeed[1]} anchor="end" max={135} />

      {/* gateway ↔ mstrader */}
      <path
        d="M365,340 L365,450"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerStart="url(#mst-arrow)"
        markerEnd="url(#mst-arrow)"
      />
      <Label x={375} y={385} text={d.orders[0]} anchor="start" max={118} />
      <Label x={375} y={399} text={d.orders[1]} anchor="start" max={118} />

      {/* mdcollector → msmarketdata */}
      <path
        d="M635,410 L635,450"
        fill="none"
        stroke={line}
        strokeWidth={1.5}
        markerEnd="url(#mst-arrow)"
      />
      <Label x={645} y={435} text={d.ticks} anchor="start" max={130} />

      {/* msmarketdata → gateway (read-only) */}
      <path
        d="M540,485 L500,485 L500,325 L460,325"
        fill="none"
        stroke="rgba(255,255,255,0.45)"
        strokeWidth={1.3}
        strokeDasharray="5 4"
        markerEnd="url(#mst-arrow)"
      />
      <Label x={492} y={420} text={d.history[0]} anchor="end" max={118} />
      <Label x={492} y={434} text={d.history[1]} anchor="end" max={118} />
    </svg>
  );
};

export default MsTraderDiagram;
